import type { ModelAssetNode } from '../assets/model-asset-types'
import type { AssemblyDocument } from '../assembly-document/assembly-document-types'
import {
  listAssemblyDocumentDirectChildReferences,
  type AssemblyDocumentDirectChildReference,
} from '../assembly-document/assembly-document-operations'
import { parseRepositoryPuppyNodeWhere } from '../assembly-document/repository-puppy-node-where'

/**
 * root `.puppy` 装配直系子节点差异分析输入。
 */
export interface AnalyzeRootPuppyAssemblyDirectChildDiffInput {
  /** 当前项目模型根节点。 */
  readonly root: ModelAssetNode
  /** 根节点 `_assembly.json` 解析得到的装配文档。 */
  readonly document: AssemblyDocument
  /** standard Puppy 使用的精确 node_where coverage。 */
  readonly standardPuppyCoverage?: {
    /** 当前根节点实际加载的仓库 `.puppy` 文件路径。 */
    readonly packageFileId: string
    /** 已由 project-model 解析并物化的包内根直系 assemblyNodeId。 */
    readonly rootDirectAssemblyNodeIds: ReadonlySet<string>
  }
  /** complete GLB 使用的 occurrence coverage。 */
  readonly completeGlbCoverage?: {
    /** 选中 GLB 根折叠后的业务一级 occurrence instanceId。 */
    readonly rootDirectInstanceIds: ReadonlySet<string>
  }
}

/**
 * root `.puppy` 装配直系子节点差异分析结果。
 */
export interface RootPuppyAssemblyDirectChildDiff {
  /** 装配文档中的全部外部直系子节点引用。 */
  readonly directChildReferences: readonly AssemblyDocumentDirectChildReference[]
  /** 通过 repository node_where 精确确认已被包内根直系节点承载的引用。 */
  readonly coveredReferences: readonly AssemblyDocumentDirectChildReference[]
  /** 未能精确确认覆盖、需要由调用方继续按版本状态判断的候选引用。 */
  readonly needsAddReferences: readonly AssemblyDocumentDirectChildReference[]
  /** instance 已覆盖但持久化 node_where 与当前包不一致的引用。 */
  readonly bridgeMismatchReferences: readonly AssemblyDocumentDirectChildReference[]
}

/**
 * 从外部引用的 repository `node_where` 解析已覆盖的包内根直系装配节点 ID。
 *
 * 该函数只接受显式、精确的 `<packageFileId>/<assemblyNodeId>` 桥接关系；任何类型、
 * 路径或集合命中失败都返回 null，不使用名称、实例、nodeKey 或兄弟顺序兜底。
 *
 * @param input 原始 node_where、当前包路径与包内根直系节点集合。
 * @returns 精确命中的完整 assemblyNodeId；否则返回 null。
 */
export function resolveCoveredRootDirectAssemblyNodeId(input: {
  readonly nodeWhere: unknown
  readonly packageFileId: string
  readonly rootDirectAssemblyNodeIds: ReadonlySet<string>
}): string | null {
  if (typeof input.nodeWhere !== 'string') {
    return null
  }

  let assemblyNodeId: string | null
  try {
    assemblyNodeId = parseRepositoryPuppyNodeWhere(input.nodeWhere, input.packageFileId)
  } catch {
    // 不可信加载边界中的坏路径只代表“无法证明覆盖”，不得中断其它候选的恢复。
    return null
  }

  return assemblyNodeId && input.rootDirectAssemblyNodeIds.has(assemblyNodeId)
    ? assemblyNodeId
    : null
}

/**
 * 分析 root `.puppy` 与根装配文档外部 direct child 的一级覆盖差异。
 *
 * @param input 当前根、装配文档和精确的包内 project-model 上下文。
 * @returns 仅包含 covered / needsAdd 两态的纯分析结果。
 */
export function analyzeRootPuppyAssemblyDirectChildDiff(
  input: AnalyzeRootPuppyAssemblyDirectChildDiffInput,
): RootPuppyAssemblyDirectChildDiff {
  const directChildReferences = listAssemblyDocumentDirectChildReferences(input.document, input.root.nodeKey)
  const coveredReferences: AssemblyDocumentDirectChildReference[] = []
  const needsAddReferences: AssemblyDocumentDirectChildReference[] = []
  const bridgeMismatchReferences: AssemblyDocumentDirectChildReference[] = []
  const completeCoverage = input.completeGlbCoverage
  const standardCoverage = input.standardPuppyCoverage
  const seenCompleteInstanceIds = new Map<string, string>()

  for (const reference of directChildReferences) {
    const instanceId = reference.instanceId?.trim() ?? ''

    if (completeCoverage && instanceId) {
      const previousNodeKey = seenCompleteInstanceIds.get(instanceId)
      if (previousNodeKey) {
        throw new Error(
          `complete GLB direct child instance_id 重复：instanceId=${instanceId}, nodeKeys=${previousNodeKey},${reference.nodeKey}`,
        )
      }
      seenCompleteInstanceIds.set(instanceId, reference.nodeKey)

      if (!completeCoverage.rootDirectInstanceIds.has(instanceId)) {
        // complete occurrence 图的 instance_id 只在当前选中根和当前包作用域内比较，不作全局身份推断。
        needsAddReferences.push(reference)
        continue
      }

      coveredReferences.push(reference)
      if (standardCoverage && reference.nodeWhere !== undefined) {
        const coveredAssemblyNodeId = resolveCoveredRootDirectAssemblyNodeId({
          nodeWhere: reference.nodeWhere,
          packageFileId: standardCoverage.packageFileId,
          rootDirectAssemblyNodeIds: standardCoverage.rootDirectAssemblyNodeIds,
        })
        if (!coveredAssemblyNodeId) {
          bridgeMismatchReferences.push(reference)
        }
      }
      continue
    }

    if (standardCoverage) {
      const coveredAssemblyNodeId = resolveCoveredRootDirectAssemblyNodeId({
        nodeWhere: reference.nodeWhere,
        packageFileId: standardCoverage.packageFileId,
        rootDirectAssemblyNodeIds: standardCoverage.rootDirectAssemblyNodeIds,
      })
      if (coveredAssemblyNodeId) {
        coveredReferences.push(reference)
        continue
      }
    }

    needsAddReferences.push(reference)
  }

  return {
    directChildReferences,
    coveredReferences,
    needsAddReferences,
    bridgeMismatchReferences,
  }
}
